import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { COLUMNS } from "./constants";
import usePagination from "../../hooks/usePagination";
import { FilterAutocomplete, Table } from "../../components/ui";
import { complianceSearchActions } from "../../reducers/complianceSearch";
import { useComplianceSearchContext } from "../../context";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../api/compliance";
import { appendSearchParamsToQuery, TitleUpdated } from "../ComplianceSearch/utils";
import { WBP_TAB_TITLES } from "../ComplianceSearch/components/WatchListBanPnlTab/constants";
import { SectionTitle } from "../ComplianceSearch/styledComponents";

const HeliosNotesSearch: React.FC = () => {
  const dispatch = useDispatch();
  const pagination = usePagination();

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { search } = tabsTablesSearchState.watchListBanPnlTab.heliosNotesSearchTable;

  const {
    loading,
    data: { results, updated },
  } = useSelector((state) => state.complianceSearch.watchlistNotes);

  const { isError, error } = useQuery({
    retry: false,
    queryKey: [
      COMPLIANCE_API_URLS.getSearchNotes,
      Object.values(search),
      pagination.page,
      pagination.limit,
    ],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());
      appendSearchParamsToQuery(search, query);

      dispatch(complianceSearchActions.fetchWatchlistNotesRequest());
      const response = await ComplianceAPI.getSearchNotes(query.toString(), signal);
      pagination.setCount(response.data.results.count);
      dispatch(complianceSearchActions.fetchWatchlistNotesSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching helios notes search", error);
      dispatch(complianceSearchActions.fetchWatchlistNotesFailed());
    }
  }, [isError, error]);

  useEffect(() => {
    pagination.setPage(0);
  }, [search, pagination.limit]);

  return (
    <SectionTitle title="Watchlist notes">
      <div>
        <FilterAutocomplete
          search={search}
          setSearch={(values) => {
            setTabsTablesSearchState({
              ...tabsTablesSearchState,
              watchListBanPnlTab: {
                ...tabsTablesSearchState.watchListBanPnlTab,
                heliosNotesSearchTable: {
                  search: values,
                },
              },
            });
          }}
          filters={{
            Name: "names",
            Email: "email",
            "Entries ID": "compliance_entries_id",
          }}
        />
      </div>

      <Table
        columns={COLUMNS}
        title={<TitleUpdated value={updated?.measure_timestamp} />}
        loading={loading}
        pagination={pagination}
        extractKey={(row) => row.id || (row.ids as []).join(", ")}
        data={results.rows}
        csv_table_name={WBP_TAB_TITLES.notesSearchTable}
      />
    </SectionTitle>
  );
};

export default HeliosNotesSearch;
